import React, { useCallback, useMemo, useState } from 'react'

import Flex from '@react-css/flex'
import { Column } from 'react-table'
import styled from 'styled-components'

import { deleteSchedule } from '../../../../../api/adminBuildingFetches'
import iconPlus from '../../../../../assets/images/icon_plus.svg'
import iconTrash from '../../../../../assets/images/icon_trash_red.svg'
import { useBuildingContext } from '../../../../../contexts/buildingContext'
import { useNotifications } from '../../../../../contexts/notificationProvider'
import { useNavigation } from '../../../../../hooks/useNavigation'
import { useViewportWidth } from '../../../../../hooks/useViewportWidth'
import { BREAKPOINTS } from '../../../../../styles/breakpoints'
import { ConfirmationModal } from '../../../../common/Confirmation/Confirmation'
import { LoadingIndicator } from '../../../../common/LoadingIndicator'
import { Table } from '../../../../common/Table/Table'
import { AddProjectButton } from '../../../buildings_page/components/AddProjectButton'
import { useSchedulesQuery } from '../../hooks/adminBuildingQueries'

import { useManageSchedulesContext } from './ManageSchedulesContext'

export interface IScheduleDashboardEntry {
  id: number | 'new'
  name: string
  frequency: string
  frequencyFormatted: string
  start_date: string
  till_date: string
  time: string
  formatted_time?: string
  converted_time?: Date
  nextScan?: Date
  nextScanFormatted?: string
  nextScanExpired?: boolean
  nextScanLessThan24HoursAway?: boolean
}

export interface ISchedule {
  id: number | 'new'
  name: string
  frequency: string
  frequencyFormatted?: string
  start_date: Date | string
  till_date: Date | string
  time: string
  nextScanFormatted?: string
}

interface ISchedulesDashboardProps {
  firstFloorName: string
}

export const SchedulesDashboard = ({
  firstFloorName,
}: ISchedulesDashboardProps) => {
  const { updateBuilding, state: buildingState } = useBuildingContext()
  const viewportWidth = useViewportWidth()
  const isMobileLayout = viewportWidth < BREAKPOINTS.tablet

  const { canCreateSchedules, canDeleteSchedules } = useManageSchedulesContext()

  const [confirmation, setConfirmation] = useState<boolean>(false)
  const [scheduleToDelete, setScheduleToDelete] = useState<
    number | 'new' | null
  >(null)

  const { navigateToManageSchedules } = useNavigation()

  const { addNotification } = useNotifications()

  const updateSchedules = useCallback(
    (schedules: ISchedule[]) => {
      updateBuilding({
        schedules,
      })
    },
    [updateBuilding],
  )

  const { isLoading: schedulesLoading } = useSchedulesQuery(updateSchedules)

  const onClickAddSchedule = () => {
    navigateToManageSchedules(firstFloorName, 'new')
  }

  const onClickScheduleRow = (schedule: ISchedule) => {
    navigateToManageSchedules(firstFloorName, schedule.id)
  }

  const onDeleteSchedule = async () => {
    if (scheduleToDelete !== null) {
      try {
        const deletedSchedule = await deleteSchedule(
          buildingState.projectId,
          scheduleToDelete,
        )
        const updatedSchedules = buildingState.schedules.filter(
          (schedule: ISchedule) => schedule.id !== deletedSchedule.id,
        )

        updateSchedules(updatedSchedules)
        addNotification('Schedule deleted successfully', 'success')
      } catch (err) {
        // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
        console.log('deleteSchedule==>>', err)
        addNotification('Error deleting schedule', 'error')
      }

      setScheduleToDelete(null)
    }
  }

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent, schedule: ISchedule) => {
      e.preventDefault()
      e.stopPropagation()
      setConfirmation(true)
      setScheduleToDelete(schedule.id)
    },
    [],
  )

  const data = useMemo(() => {
    return buildingState.schedules.map((schedule: ISchedule) => {
      const onClickDelete = (e: React.MouseEvent<HTMLDivElement>) => {
        handleDeleteClick(e, schedule)
      }

      return {
        ...schedule,
        delete: (
          <AddProjectButton
            text="Delete"
            icon={iconTrash}
            onClick={onClickDelete}
            buttonStyle={{ color: '#ff5252', border: '1px solid #ff5252' }}
          />
        ),
      }
    })
  }, [buildingState.schedules, handleDeleteClick])

  const sortDates = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper row types
    (rowA: any, rowB: any, columnId: string, desc?: boolean) => {
      if (rowA.original.nextScanExpired && rowB.original.nextScanExpired) {
        return 0
      } else if (rowA.original.nextScanExpired) {
        return desc ? -1 : 1
      } else if (rowB.original.nextScanExpired) {
        return desc ? 1 : -1
      }

      const dateA: Date = rowA.original.nextScan
      const dateB: Date = rowB.original.nextScan

      const dateALarger = dateA.getTime() - dateB.getTime() >= 0

      return dateALarger ? 1 : -1
    },
    [],
  )

  const columns = useMemo(
    (): Column[] => [
      { Header: 'Name', accessor: 'name', width: '40%' },
      { Header: 'Frequency', accessor: 'frequencyFormatted', width: '20%' },
      {
        Header: 'Next Scan',
        accessor: 'nextScanFormatted',
        sortType: sortDates,
        width: '25%',
      },
      ...(canDeleteSchedules
        ? [
            {
              Header: 'Delete',
              accessor: 'delete',
              width: '15%',
              disableSortBy: true,
            },
          ]
        : []),
    ],
    [sortDates, canDeleteSchedules],
  )

  return (
    <>
      {schedulesLoading && <LoadingIndicator />}

      {!schedulesLoading && (
        <>
          {canCreateSchedules && (
            <AddActionBar>
              <AddProjectButton
                text="Add Schedule"
                icon={iconPlus}
                onClick={onClickAddSchedule}
              />
            </AddActionBar>
          )}

          <TableScrollWrap>
            {isMobileLayout ? (
              <MobileScheduleList>
                {buildingState.schedules.map((schedule: ISchedule) => (
                  <MobileScheduleCard
                    key={schedule.id}
                    onClick={() => onClickScheduleRow(schedule)}
                  >
                    <MobileScheduleName>{schedule.name}</MobileScheduleName>
                    <MobileScheduleMeta>
                      <MobileScheduleLabel>Frequency</MobileScheduleLabel>
                      <span>
                        {schedule.frequencyFormatted ?? schedule.frequency}
                      </span>
                    </MobileScheduleMeta>
                    <MobileScheduleMeta>
                      <MobileScheduleLabel>Next Scan</MobileScheduleLabel>
                      <span>{schedule.nextScanFormatted ?? '—'}</span>
                    </MobileScheduleMeta>
                    {canDeleteSchedules && (
                      <AddProjectButton
                        text="Delete"
                        icon={iconTrash}
                        onClick={e => handleDeleteClick(e, schedule)}
                        buttonStyle={{
                          color: '#ff5252',
                          border: '1px solid #ff5252',
                          marginTop: 8,
                        }}
                      />
                    )}
                  </MobileScheduleCard>
                ))}
              </MobileScheduleList>
            ) : (
              <Table
                data={data}
                columns={columns}
                onClickRow={onClickScheduleRow}
                headerStyles={{ textAlign: 'left' }}
                bodyStyles={{ textAlign: 'left', paddingLeft: '5px' }}
                initialSortBy={[
                  {
                    id: 'nextScanFormatted',
                    desc: false,
                  },
                ]}
              />
            )}
          </TableScrollWrap>
        </>
      )}

      <ConfirmationModal
        isOpen={confirmation}
        setIsOpen={setConfirmation}
        message="Are you sure you would like to delete this schedule?"
        onConfirm={onDeleteSchedule}
      />
    </>
  )
}

const AddActionBar = styled(Flex)`
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
`

const TableScrollWrap = styled.div`
  width: 100%;
  overflow-x: auto;
`

const MobileScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const MobileScheduleCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  width: 100%;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 16px;
  cursor: pointer;
  box-sizing: border-box;
`

const MobileScheduleName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 12px;
`

const MobileScheduleMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #334155;
  margin-bottom: 6px;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const MobileScheduleLabel = styled.span`
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
`
